/**
 * REST client for the user-files API (`GET /user-files` on the async HTTP API).
 * Backs the console's Files panel: list the signed-in user's persistent
 * documents as a folder tree.
 *
 * Files are **user-scoped, not agent-scoped** — the backend keys them by the
 * Cognito `sub` from the bearer token, so any agent's documents show up in one
 * listing. Auth + error handling mirror `sessionApi.ts`: the Cognito access
 * token as a bearer; a user-friendly message on a non-OK response with the
 * status detail logged for debugging.
 *
 * This client only lists. Downloads (`doc_share_url`) and deletes (`doc_delete`,
 * behind a confirmation gate) go through the agent chat, not this endpoint.
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import { requestTimeout } from '../config/requestTimeout.ts';
import { ApiRequestError } from './apiRequestError.ts';

/** A file leaf in the document tree. Mirrors the Lambda's `FileNode`. */
export interface FileNode {
  readonly type: 'file';
  readonly name: string;
  /** Path relative to the user's documents root, e.g. `reports/q1.md`. */
  readonly path: string;
  readonly bytes: number;
  /** ISO-8601 last-modified instant; '' when unknown. */
  readonly lastModified: string;
  /** True for a surfaced ephemeral file (email attachment or console upload). */
  readonly ephemeral?: boolean;
  /** ISO-8601 instant the file auto-expires; set only when `ephemeral`. */
  readonly expiresAt?: string;
}

/** A folder node holding nested files/folders. Mirrors the Lambda's `FolderNode`. */
export interface FolderNode {
  readonly type: 'folder';
  readonly name: string;
  readonly path: string;
  readonly children: readonly TreeNode[];
}

export type TreeNode = FileNode | FolderNode;

async function authHeader(): Promise<Record<string, string>> {
  const jwt = await getAccessToken();
  return { Authorization: `Bearer ${jwt}` };
}

/** A short-lived presigned download link for one file. */
export interface DownloadLink {
  readonly url: string;
  readonly name: string;
  readonly expiresAt: string;
}

/**
 * Mint a presigned download link for one of the user's files — DIRECTLY from the
 * `/user-files` Lambda, not via the agent. Routing downloads through the chat
 * proved unreliable (the model would answer from context and skip the
 * `doc_share_url` tool, producing a stale/fabricated link), so the Files panel
 * signs the URL itself, sub-scoped server-side.
 */
export async function getDownloadUrl(path: string): Promise<DownloadLink> {
  const response = await fetch(
    `${asyncApiConfig.baseUrl}/user-files?download=${encodeURIComponent(path)}`,
    { method: 'GET', headers: await authHeader(), signal: requestTimeout() },
  );
  if (!response.ok) {
    console.error(`[userFilesApi] download failed (${response.status})`, await safeText(response));
    throw new ApiRequestError('Could not get a download link for that file.', `http-${response.status}`);
  }
  return (await response.json()) as DownloadLink;
}

/** List the signed-in user's persistent documents as a folder tree. */
export async function listUserFiles(): Promise<readonly TreeNode[]> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/user-files`, {
    method: 'GET',
    headers: await authHeader(),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    console.error(`[userFilesApi] list failed (${response.status})`, await safeText(response));
    throw new Error('Could not load your files.');
  }
  const body = (await response.json()) as { tree?: readonly TreeNode[] };
  return body.tree ?? [];
}

/** Best-effort body read for an error log — never throws. */
async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

/** A minted upload URL plus the path the file will occupy. */
export interface UploadTicket {
  readonly url: string;
  /** Relative document path, e.g. `tmp/uploads/a1/notes.md`. */
  readonly path: string;
  readonly expiresAt: string;
}

/** Thrown when the user cancelled — distinct from a failure, so the UI can
 *  stay silent instead of reporting an error nobody experienced. */
export class UploadAbortedError extends Error {
  constructor() {
    super('upload cancelled');
    this.name = 'UploadAbortedError';
  }
}

/**
 * Ask the backend for a presigned PUT. The browser sends only a filename, a
 * size and a content type — the Lambda composes the S3 key from the JWT `sub`,
 * so a client can never choose where its bytes land.
 */
export async function requestUploadUrl(file: File): Promise<UploadTicket> {
  const response = await fetch(`${asyncApiConfig.baseUrl}/user-files?upload=1`, {
    method: 'POST',
    headers: { ...(await authHeader()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ filename: file.name, bytes: file.size, contentType: file.type }),
    signal: requestTimeout(),
  });
  if (!response.ok) {
    const detail = await safeText(response);
    console.error(`[userFilesApi] upload presign failed (${response.status})`, detail);
    // The backend's reasons are already user-appropriate ('file too large',
    // 'empty file'), so surface them rather than a generic message.
    throw new Error(uploadErrorMessage(response.status, detail));
  }
  return (await response.json()) as UploadTicket;
}

/** Map a presign rejection to something worth showing a person. */
function uploadErrorMessage(status: number, detail: string): string {
  try {
    const reason = (JSON.parse(detail) as { error?: string }).error;
    if (typeof reason === 'string' && reason.length > 0) {
      return `Could not upload: ${reason}.`;
    }
  } catch {
    // Not JSON — fall through to the status-based message.
  }
  return `Could not upload that file (${status}).`;
}

export interface PutSignedFileOptions {
  readonly url: string;
  readonly file: File;
  /** Sanitized basename from the ticket's path — must match what was signed. */
  readonly filename: string;
  readonly contentType: string;
  readonly onProgress: (fraction: number) => void;
  readonly signal?: AbortSignal;
}

/**
 * Upload the bytes to the presigned URL.
 *
 * **The only place that knows the transport is a PUT.** If the size cap ever
 * turns out to need a presigned POST instead, this function is all that
 * changes.
 *
 * `XMLHttpRequest` rather than `fetch`: `fetch` cannot report upload progress,
 * and a progress indicator is a requirement.
 */
export function putSignedFile(opts: PutSignedFileOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', opts.url, true);
    // Unsigned, but S3 stores it — and a non-safelisted value like
    // application/pdf is why the request preflights at all.
    xhr.setRequestHeader('content-type', opts.contentType);
    // A SIGNED header: it must match what the Lambda signed byte for byte, so
    // it is rebuilt from the server's own sanitized path rather than from the
    // local File's name. It also forces a later presigned GET to download
    // rather than render, so an uploaded .html cannot execute on an AWS origin.
    xhr.setRequestHeader('content-disposition', `attachment; filename="${opts.filename}"`);
    // Deliberately NOT set here:
    //   x-amz-tagging  — the presigner hoisted it into the URL's query string;
    //                    sending it as an unsigned header risks a mismatch.
    //   content-length — a forbidden header name; the browser sets it from the
    //                    body, which is exactly what makes the signed length a
    //                    real size cap the client cannot lie about.
    xhr.upload.onprogress = (e: ProgressEvent): void => {
      if (e.lengthComputable && e.total > 0) {
        opts.onProgress(e.loaded / e.total);
      }
    };
    xhr.onload = (): void => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Upload rejected by storage (${xhr.status}).`));
    };
    xhr.onerror = (): void => reject(new Error('Upload failed (network error).'));
    xhr.onabort = (): void => reject(new UploadAbortedError());
    opts.signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(opts.file);
  });
}
